/* eslint-disable react/prop-types */

import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { keepPreviousData } from "@tanstack/react-query";
import {
  Box,
  Tabs,
  Tab,
  Card,
  CardContent,
  CardActionArea,
  Button,
  Typography,
  CardHeader,
  Avatar,
  ToggleButtonGroup,
  ToggleButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Divider,
  Stack,
  Alert,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import Toast from "@/components/toast";
import PageHeader from "@/components/PageHeader";

import MapIcon from "@mui/icons-material/Map";
import CloseIcon from "@mui/icons-material/Close";
import useAuthenticatedQuery from "@/hooks/useAuthenticatedQuery";
import useGetDetailledObservation from "@/hooks/useGetDetailledObservation";
import ObservationDetailsDialog from "./observationDetailsDialog";
import { useSession } from "@/hooks/useSession";
import { styled, useTheme, alpha } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  APIProvider,
  AdvancedMarker,
  Map as GoogleMap,
  useMap,
  Pin,
  InfoWindow,
} from "@vis.gl/react-google-maps";
import { convertUTCToLocal, getOffsetPosition } from "@/lib/utils/dateUtils";
import { MarkerClusterer } from "@googlemaps/markerclusterer";
import { Circle } from "@/components/circle";
import { useTranslation } from "react-i18next";
import useApiErrorHandler from "@/hooks/useApiErrorHandler";

// Styled Components
const StyledCard = styled(Card)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  height: "100%",
  transition: "box-shadow 0.2s ease-in-out",
  "&:hover": {
    boxShadow: theme.shadows[6],
  },
  position: "relative",
  backgroundColor: theme.palette.background.paper,
}));

const LoadingOverlay = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(255, 255, 255, 0.8)",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  zIndex: 10,
  borderRadius: theme.shape.borderRadius,
}));

const MapContainerStyle = {
  width: "100%",
  height: "600px",
};

const londonCenter = {
  lat: 51.5074,
  lng: -0.1278,
};

// Helpers
function a11yProps(idx) {
  return {
    id: `tab-${idx}`,
    "aria-controls": `tabpanel-${idx}`,
  };
}

// Priority Color Mapping - uses priority code (P1, P2, P3) which is language-independent
const priorityColorMap = {
  "P1": "#FF0000", // Red for High
  "P2": "#FFA500", // Orange for Medium
  "P3": "#008000", // Green for Low
};

// Helper function to extract priority code from priority name
function getPriorityColor(priorityName) {
  if (!priorityName) return "#FBBC04"; // Default yellow
  
  // Extract P1, P2, or P3 from the priority name (e.g., "P1 - High", "P1 - Yüksek", etc.)
  const match = priorityName.match(/^(P\d+)/i);
  if (match) {
    const code = match[1].toUpperCase();
    return priorityColorMap[code] || "#FBBC04";
  }
  
  return "#FBBC04"; // Default yellow
}

function TabPanel({ children, value, index }) {
  return (
    <div role="tabpanel" hidden={value !== index} {...a11yProps(index)}>
      {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
    </div>
  );
}

// Inline ObservationCard
function ObservationCard({ obs, loading, onView, t }) {
  const theme = useTheme();

  if (!obs?.history_steps?.length) {
    return null;
  }

  const lastStep = obs.history_steps[obs.history_steps.length - 1];
  const rawStatus = lastStep?.status;
  const rawStatusDate = lastStep?.created_at;
  const latest = rawStatus
    ? rawStatus.charAt(0).toUpperCase() + rawStatus.slice(1).toLowerCase()
    : t("myToDos.unknownStatus");

  return (
    <StyledCard
      elevation={2}
      sx={{
        borderRadius: 3,
        border: `1px solid ${theme.palette.divider}`,
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        "&:hover": {
          elevation: 8,
          borderColor: theme.palette.primary.main,
          transform: "translateY(-2px)",
          boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
        },
      }}
    >
      {loading && (
        <LoadingOverlay>
          <CircularProgress size={32} color="primary" />
          <Typography variant="body2" color="text.secondary" mt={1}>
            {t("myToDos.loadingDetails")}
          </Typography>
        </LoadingOverlay>
      )}

      <CardActionArea
        onClick={() => onView(obs.id)}
        sx={{
          flexGrow: 1,
          borderRadius: 3,
        }}
      >
        <CardHeader
          avatar={
            <Avatar
              src={obs.author?.avatar_url}
              alt={obs.author?.username}
              sx={{
                width: 48,
                height: 48,
                border: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`,
              }}
            />
          }
          title={
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: theme.palette.text.primary,
                fontSize: "1.1rem",
              }}
            >
              {obs.workflow?.name || t("myToDos.noWorkflow")}
            </Typography>
          }
          subheader={
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontWeight: 500 }}
            >
              {convertUTCToLocal(obs.created_at)}
            </Typography>
          }
          sx={{ pb: 1 }}
        />

        <CardContent sx={{ pt: 0, pb: 3 }}>
          <Stack spacing={2}>
            {/* Site and Priority Info */}
            <Box
              sx={{
                bgcolor: alpha(theme.palette.background.default, 0.5),
                borderRadius: 2,
                p: 2,
              }}
            >
              <Stack spacing={1.5}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontWeight: 500 }}
                  >
                    {t("myToDos.siteLabel")}:
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.text.primary,
                    }}
                  >
                    {obs.site?.name || t("myToDos.unknownSite")}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontWeight: 500 }}
                  >
                    {t("myToDos.priorityLabel")}:
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.text.primary,
                    }}
                  >
                    {obs.observation_priority?.name ||
                      t("myToDos.unknownPriority")}
                  </Typography>
                </Box>
              </Stack>
            </Box>

            {/* Description */}
            {obs.description && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  display: "-webkit-box",
                  WebkitBoxOrient: "vertical",
                  WebkitLineClamp: 2,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  lineHeight: 1.5,
                  fontStyle: "italic",
                }}
              >
                &ldquo;{obs.description}&rdquo;
              </Typography>
            )}

            {/* Timeline */}
            <Divider sx={{ borderColor: alpha(theme.palette.divider, 0.5) }} />
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontWeight: 500 }}
              >
                {t("myToDos.statusAtLabel", { status: latest })}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                }}
              >
                {convertUTCToLocal(rawStatusDate)}
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </CardActionArea>
    </StyledCard>
  );
}

// PoiMarkers Component
function PoiMarkers({ pois, observations, onMarkerClick, t }) {
  const map = useMap();
  const [markers, setMarkers] = useState({});
  const [listeners, setListeners] = useState({});
  const clusterer = useRef(null);
  const [circleCenter, setCircleCenter] = useState(null);
  const [infoWindowPosition, setInfoWindowPosition] = useState(null);
  const [infoWindowObs, setInfoWindowObs] = useState([]);

  const obsByLocation = useMemo(() => {
    const grouped = {};
    observations.forEach((obs) => {
      const lat = obs.site?.latitude;
      const lng = obs.site?.longitude;
      if (Number.isFinite(lat) && Number.isFinite(lng)) {
        const key = `${lat},${lng}`;
        if (!grouped[key]) {
          grouped[key] = [];
        }
        grouped[key].push(obs);
      }
    });
    return grouped;
  }, [observations]);

  const handleMarkerClick = useCallback(
    (obsId, lat, lng, obsAtLocation) => {
      map.panTo({ lat, lng });
      setCircleCenter({ lat, lng });
      if (obsAtLocation.length > 1) {
        setInfoWindowPosition({ lat, lng });
        setInfoWindowObs(obsAtLocation);
      } else {
        setInfoWindowPosition(null);
        setInfoWindowObs([]);
        onMarkerClick(obsId);
      }
    },
    [map, onMarkerClick]
  );

  const handleInfoWindowClose = () => {
    setInfoWindowPosition(null);
    setInfoWindowObs([]);
  };

  useEffect(() => {
    if (!map || clusterer.current) return;
    clusterer.current = new MarkerClusterer({ map });
  }, [map]);

  useEffect(() => {
    clusterer.current?.clearMarkers();
    clusterer.current?.addMarkers(
      Object.values(markers).filter((marker) => marker)
    );
  }, [markers]);

  const setMarkerRef = (marker, key) => {
    if (!key) {
      return;
    }
    if (marker && markers[key]) return;
    if (!marker && !markers[key]) return;

    setMarkers((prev) => {
      const newMarkers = { ...prev };
      if (marker) {
        newMarkers[key] = marker;
      } else {
        delete newMarkers[key];
      }
      return newMarkers;
    });

    setListeners((prev) => {
      const newListeners = { ...prev };
      if (marker) {
        const [, id, lat, lng] = key.split("|");
        const locKey = `${lat},${lng}`;
        const obsAtLocation = obsByLocation[locKey] || [];
        const listener = window.google.maps.event.addListener(
          marker,
          "gmp-click",
          () =>
            handleMarkerClick(
              id,
              parseFloat(lat),
              parseFloat(lng),
              obsAtLocation
            )
        );
        newListeners[key] = listener;
      } else {
        if (newListeners[key]) {
          window.google.maps.event.removeListener(newListeners[key]);
        }
        delete newListeners[key];
      }
      return newListeners;
    });
  };

  useEffect(() => {
    return () => {
      Object.values(listeners).forEach((listener) => {
        if (listener) {
          window.google.maps.event.removeListener(listener);
        }
      });
      clusterer.current?.clearMarkers();
    };
  }, [listeners]);

  return (
    <>
      <Circle
        radius={800}
        center={circleCenter}
        strokeColor="#0c4cb3"
        strokeOpacity={1}
        strokeWeight={3}
        fillColor="#3b82f6"
        fillOpacity={0.3}
      />
      {pois.map((poi) => {
        const obs = observations.find((o) => o.id === poi.key);
        if (!obs || !poi.key || !poi.location?.lat || !poi.location?.lng) {
          return null;
        }
        const locKey = `${poi.location.lat},${poi.location.lng}`;
        const obsAtLocation = obsByLocation[locKey] || [obs];
        const index = obsAtLocation.findIndex((o) => o.id === poi.key);
        const offsetPosition = getOffsetPosition(
          poi.location,
          index,
          obsAtLocation.length
        );
        const priorityColor = getPriorityColor(obs.observation_priority?.name);
        return (
          <AdvancedMarker
            key={`obs|${poi.key}|${poi.location.lat}|${poi.location.lng}`}
            position={offsetPosition}
            ref={(marker) =>
              setMarkerRef(
                marker,
                `obs|${poi.key}|${poi.location.lat}|${poi.location.lng}`
              )
            }
          >
            <Pin
              background={priorityColor}
              glyphColor="#000"
              borderColor="#000"
            />
          </AdvancedMarker>
        );
      })}
      {infoWindowPosition && infoWindowObs.length > 0 && (
        <InfoWindow
          position={infoWindowPosition}
          onCloseClick={handleInfoWindowClose}
        >
          <Box>
            <Typography variant="body2" fontWeight="bold">
              {t("myToDos.infoWindowTitle")}
            </Typography>
            {infoWindowObs.map((obs) => (
              <Typography
                key={obs.id}
                variant="body2"
                sx={{
                  cursor: "pointer",
                  color: "blue",
                  textDecoration: "underline",
                }}
                onClick={() =>
                  handleMarkerClick(
                    obs.id,
                    obs.site.latitude,
                    obs.site.longitude,
                    [obs]
                  )
                }
              >
                {obs.author.first_name} {obs.author.last_name} -{" "}
                {obs.observation_priority?.name || t("myToDos.unknownPriority")}
              </Typography>
            ))}
          </Box>
        </InfoWindow>
      )}
    </>
  );
}

// ObservationsMap Component
function ObservationsMap({
  observations,
  markerLocations,
  mapsLoaded,
  onMarkerClick,
  t,
}) {
  const map = useMap();

  useEffect(() => {
    if (
      !map ||
      !mapsLoaded ||
      !window.google?.maps ||
      typeof window.google.maps.LatLngBounds !== "function" ||
      !markerLocations.length
    ) {
      return;
    }

    const bounds = new window.google.maps.LatLngBounds();
    markerLocations.forEach((poi) => {
      if (
        Number.isFinite(poi.location.lat) &&
        Number.isFinite(poi.location.lng)
      ) {
        bounds.extend({
          lat: poi.location.lat,
          lng: poi.location.lng,
        });
      }
    });

    if (!bounds.isEmpty()) {
      map.fitBounds(bounds);
    } else {
      map.setCenter(londonCenter);
      map.setZoom(14);
    }
  }, [map, markerLocations, mapsLoaded]);

  return (
    <GoogleMap
      style={MapContainerStyle}
      defaultCenter={londonCenter}
      defaultZoom={14}
      mapId="YOUR_MAP_ID"
      zoomControl={true}
      gestureHandling="auto"
      minZoom={3}
      maxZoom={18}
    >
      <PoiMarkers
        pois={markerLocations}
        observations={observations}
        onMarkerClick={onMarkerClick}
        t={t}
      />
    </GoogleMap>
  );
}

export default function MyToDos() {
  const { t } = useTranslation();
  const theme = useTheme();
  const isLgDown = useMediaQuery(theme.breakpoints.down("lg"));
  const { session } = useSession();
  const [groupBy, setGroupBy] = useState("status");
  const [tabIndex, setTabIndex] = useState(0);
  const [pageTokens, setPageTokens] = useState([null]);
  const [pages, setPages] = useState([]);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [selectedObs, setSelectedObs] = useState(null);
  const [loadingStates, setLoadingStates] = useState({});
  const [mapDialogOpen, setMapDialogOpen] = useState(false);
  const [nextPageToken, setNextPageToken] = useState(null);
  const [mapsLoaded, setMapsLoaded] = useState(false);
  const [initialTotalCount, setInitialTotalCount] = useState(null);
  const { toast, setToast, handleToastClose } = useApiErrorHandler();

  const currentToken = pageTokens[currentPageIndex];

  const {
    data,
    isLoading,
    isFetching,
    error,
    refetch,
    isPlaceholderData,
    isError,
  } = useAuthenticatedQuery(
    ["mytodos", currentToken],
    session?.id
      ? currentToken
        ? `/api/v1/observations/user/${session.id}/assigned?cursor=${currentToken}`
        : `/api/v1/observations/user/${session.id}/assigned`
      : null,
    { enabled: !!session?.id, placeholderData: keepPreviousData }
  );

  useEffect(() => {
    if (isPlaceholderData) return;
    if (isError) return;
    if (!data || !Array.isArray(data.records)) return;

    const sanitized = data.records.filter((r) => r && r.id);

    if (currentToken == null) {
      setPages([sanitized]);
      setCurrentPageIndex(0);
      setInitialTotalCount(
        typeof data.total_count === "number"
          ? data.total_count
          : Number(data.total_count) || 0
      );
    } else {
      setPages((prev) => {
        const existingIds = new Set(
          prev.flat().filter(Boolean).map((r) => r.id).filter(Boolean)
        );
        const filteredRecords = sanitized.filter((r) => !existingIds.has(r.id));
        const newPages = [...prev];
        while (newPages.length <= currentPageIndex) {
          newPages.push([]);
        }
        newPages[currentPageIndex] = filteredRecords;
        return newPages;
      });
    }

    setNextPageToken(data.next_page ?? null);

    if (data.next_page) {
      setPageTokens((prev) => {
        if (prev.includes(data.next_page)) return prev;
        return [...prev, data.next_page];
      });
    }
  }, [data, currentPageIndex, currentToken, isPlaceholderData, isError]);

  const aggregatedObservations = useMemo(() => {
    const flat = pages
      .slice(0, currentPageIndex + 1)
      .flat()
      .filter((obs) => obs && obs.id);
    const seen = new Set();
    return flat.filter((obs) => {
      if (seen.has(obs.id)) return false;
      seen.add(obs.id);
      return true;
    });
  }, [pages, currentPageIndex]);

  const hasPartialResults =
    initialTotalCount != null &&
    initialTotalCount > 0 &&
    aggregatedObservations.length < initialTotalCount;

  const { getDetailledData } = useGetDetailledObservation();
  const fetchDetails = async (id) => {
    setLoadingStates((m) => ({ ...m, [id]: true }));
    try {
      const detail = await getDetailledData(id);
      setSelectedObs(detail);
    } catch (error) {
      const apiMsg = error.response?.data?.message;

      // Your generic translation, then the API text
      const fullMessage = apiMsg
        ? `${t("myObservations.errorLoadDetails")}: ${apiMsg}`
        : t("myObservations.errorLoadDetails");

      setToast({
        open: true,
        severity: "error",
        message: fullMessage,
      });
    } finally {
      setLoadingStates((m) => ({ ...m, [id]: false }));
    }
  };

  const handleNext = () => {
    if (!nextPageToken || isFetching) return;

    const slotEmpty =
      !pages[currentPageIndex] || pages[currentPageIndex]?.length === 0;
    if (currentPageIndex > 0 && slotEmpty && isError) {
      refetch();
      return;
    }

    const nextIdx = currentPageIndex + 1;
    if (nextIdx >= pageTokens.length) return;

    setCurrentPageIndex(nextIdx);
  };

  const handleOpenMapDialog = () => {
    setMapDialogOpen(true);
  };

  const handleCloseMapDialog = () => {
    setMapDialogOpen(false);
  };

  const handleDetailsClose = () => {
    setSelectedObs(null);
    setCurrentPageIndex(0);
    setPageTokens([null]);
    setPages([]);
    setNextPageToken(null);
    setInitialTotalCount(null);
    refetch();
  };

  const allRecords = useMemo(() => {
    return Array.from(
      new Map(aggregatedObservations.map((r) => [r.id, r])).values()
    );
  }, [aggregatedObservations]);

  const finalisedRecords = useMemo(() => {
    return Array.from(
      new Map(
        aggregatedObservations
          .filter((record) => record.is_final === true)
          .map((r) => [r.id, r])
      ).values()
    );
  }, [aggregatedObservations]);

  const tabs = useMemo(() => {
    const map = new Map();
    allRecords.forEach((o) => {
      let key;
      if (groupBy === "status") {
        const steps = o.history_steps || [];
        key = steps.length
          ? steps[steps.length - 1].status
          : t("myToDos.unknownStatus");
      } else if (groupBy === "workflow") {
        key = o.workflow?.name || t("myToDos.noWorkflow");
      } else {
        key = o.observation_priority?.name || t("myToDos.unknownPriority");
      }
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(o);
    });
    const tabsArray = [
      [t("myToDos.allTab"), allRecords],
      ...[...map.entries()]
        .filter(([k]) => k !== t("myToDos.allTab"))
        .sort(([a], [b]) => a.localeCompare(b)),
      [t("myToDos.finalisedTab"), finalisedRecords],
    ];
    return tabsArray;
  }, [allRecords, finalisedRecords, groupBy, t]);

  const selectedObservations = useMemo(() => {
    return tabs[tabIndex]?.[1] || [];
  }, [tabs, tabIndex]);

  const markerLocations = useMemo(() => {
    return selectedObservations
      .filter(
        (obs) =>
          obs.id &&
          obs.site &&
          Number.isFinite(obs.site.latitude) &&
          Number.isFinite(obs.site.longitude)
      )
      .map((obs) => ({
        key: obs.id,
        location: {
          lat: obs.site.latitude,
          lng: obs.site.longitude,
        },
      }));
  }, [selectedObservations]);

  const hasMoreObservations = Boolean(nextPageToken);

  if (!session?.id) {
    return (
      <Box p={3} borderRadius={2} textAlign="center">
        <Typography variant="h6" color="text.primary">
          {t("myToDos.signInTitle")}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t("myToDos.signInMessage")}
        </Typography>
      </Box>
    );
  }

  if (isLoading && !pages.length) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="50vh"
        borderRadius={2}
      >
        <CircularProgress
          size={48}
          aria-label={t("myToDos.loadingObservationsAria")}
        />
        <Typography variant="body1" ml={2} color="text.secondary">
          {t("myToDos.loadingObservations")}
        </Typography>
      </Box>
    );
  }

  if (error && !allRecords.length && !isFetching) {
    return (
      <Box p={3} borderRadius={2} textAlign="center">
        <Typography variant="h6" color={theme.palette.error.main}>
          {t("myToDos.errorTitle")}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t("myToDos.errorMessage")}
        </Typography>
        <Button
          variant="outlined"
          color="primary"
          onClick={() => refetch()}
          sx={{ mt: 2 }}
        >
          {t("myToDos.retryButton")}
        </Button>
      </Box>
    );
  }

  if (!allRecords.length && error && isFetching) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="50vh"
        borderRadius={2}
      >
        <CircularProgress
          size={48}
          aria-label={t("myToDos.loadingObservationsAria")}
        />
        <Typography variant="body1" ml={2} color="text.secondary">
          {t("myToDos.loading")}
        </Typography>
      </Box>
    );
  }

  if (!allRecords.length && !error) {
    return (
      <Box p={3} borderRadius={2} textAlign="center">
        <Typography variant="h6" color="text.primary">
          {t("myToDos.noObservationsTitle")}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t("myToDos.noObservationsMessage")}
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: theme.palette.background.default,
        }}
      >
        {/* Compact Enterprise Header */}
        <PageHeader
          icon="✅"
          title={t("myToDos.title") || "My To-Do Observations"}
          subtitle={
            t("myToDos.subtitle") ||
            "Manage and complete your assigned observation tasks"
          }
          stats={[
            {
              label: `${allRecords.length}/${
                initialTotalCount ?? data?.total_count ?? allRecords.length
              }`,
            },
          ]}
          actions={[
            {
              label: t("myToDos.viewMapButton") || "View on Map",
              icon: <MapIcon />,
              onClick: handleOpenMapDialog,
              ariaLabel: t("myToDos.viewMapAria"),
            },
          ]}
          t={t}
          translationPrefix="myToDos"
          compact={true}
        />

        {hasPartialResults && (
          <Typography
            variant="caption"
            component="p"
            sx={{ px: { xs: 2, sm: 3 }, mb: 2, color: "text.secondary" }}
          >
            {t("myToDos.partialResultsNotice")}
          </Typography>
        )}

        {error && allRecords.length > 0 && (
          <Alert
            severity="error"
            sx={{ mb: 2, mx: { xs: 2, sm: 3 } }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => refetch()}
                sx={{ fontWeight: 600, textTransform: "none" }}
              >
                {t("myToDos.retryButton")}
              </Button>
            }
          >
            {t("myToDos.loadMoreErrorInline")}
          </Alert>
        )}

        {/* Enhanced Categorization Control */}
        <Box
          sx={{
            bgcolor: theme.palette.background.paper,
            borderRadius: 2,
            p: 2,
            mb: 2,
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: theme.shadows[1],
          }}
        >
          <ToggleButtonGroup
            value={groupBy}
            exclusive
            onChange={(_, v) => {
              if (v) {
                setGroupBy(v);
                setTabIndex(0);
              }
            }}
            size="medium"
            aria-label={t("myToDos.groupByAria")}
            sx={{
              gap: 1,
              "& .MuiToggleButton-root": {
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.875rem",
                px: 3,
                py: 1.5,
                border: `1px solid ${theme.palette.divider}`,
                transition: "all 0.2s ease",
                "&:hover": {
                  bgcolor: alpha(theme.palette.primary.main, 0.04),
                  transform: "translateY(-1px)",
                },
                "&.Mui-selected": {
                  bgcolor: theme.palette.primary.main,
                  color: theme.palette.primary.contrastText,
                  "&:hover": {
                    bgcolor: theme.palette.primary.dark,
                  },
                },
              },
            }}
          >
            <ToggleButton
              value="status"
              aria-label={t("myToDos.groupByStatus")}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="inherit">📊</Typography>
                <Typography variant="inherit">
                  {t("myToDos.statusButton") || "By Status"}
                </Typography>
              </Stack>
            </ToggleButton>
            <ToggleButton
              value="workflow"
              aria-label={t("myToDos.groupByWorkflow")}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="inherit">⚙️</Typography>
                <Typography variant="inherit">
                  {t("myToDos.workflowButton") || "By Workflow"}
                </Typography>
              </Stack>
            </ToggleButton>
            <ToggleButton
              value="priority"
              aria-label={t("myToDos.groupByPriority")}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="inherit">🔥</Typography>
                <Typography variant="inherit">
                  {t("myToDos.priorityButton") || "By Priority"}
                </Typography>
              </Stack>
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Enhanced Tabs Section */}
        <Box
          sx={{
            bgcolor: theme.palette.background.paper,
            borderRadius: 2,
            p: 1,
            mb: 3,
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: theme.shadows[1],
          }}
        >
          <Tabs
            value={tabIndex}
            onChange={(_, v) => setTabIndex(v)}
            variant={isLgDown ? "standard" : "scrollable"}
            scrollButtons={isLgDown ? false : "auto"}
            allowScrollButtonsMobile={!isLgDown}
            aria-label={t("myToDos.observationTabsAria")}
            sx={{
              "& .MuiTabs-flexContainer": {
                gap: 1,
                flexWrap: isLgDown ? "wrap" : "nowrap",
              },
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.875rem",
                minHeight: 48,
                borderRadius: 1.5,
                margin: "4px 0",
                transition: "all 0.2s ease",
                justifyContent: "center",
                maxWidth: isLgDown ? "none" : undefined,
                minWidth: isLgDown ? "auto" : undefined,
                paddingLeft: isLgDown ? 1.5 : undefined,
                paddingRight: isLgDown ? 1.5 : undefined,
                flex: isLgDown ? "0 1 auto" : "0 0 auto",
                "&:hover": {
                  bgcolor: alpha(theme.palette.primary.main, 0.04),
                },
                "&.Mui-selected": {
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  color: theme.palette.primary.main,
                },
              },
              "& .MuiTabs-indicator": {
                height: 3,
                borderRadius: "3px 3px 0 0",
                backgroundColor: theme.palette.primary.main,
              },
            }}
          >
            {tabs.map(([name, items], i) => (
              <Tab
                key={name}
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="inherit">{name}</Typography>
                    <Chip
                      label={items.length}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        bgcolor:
                          tabIndex === i
                            ? theme.palette.primary.main
                            : alpha(theme.palette.text.secondary, 0.1),
                        color:
                          tabIndex === i
                            ? theme.palette.primary.contrastText
                            : theme.palette.text.secondary,
                        border: "none",
                      }}
                    />
                  </Box>
                }
                {...a11yProps(i)}
              />
            ))}
          </Tabs>
        </Box>

        {tabs.map(([name, items], i) => (
          <TabPanel key={i} value={tabIndex} index={i} t={t}>
            {items.length > 0 ? (
              <Grid container spacing={3}>
                {items.map((o) => (
                  <Grid
                    key={o.id}
                    size={{ xs: 12, sm: 6, md: 6, lg: 4, xl: 3 }}
                  >
                    <ObservationCard
                      obs={o}
                      loading={loadingStates[o.id]}
                      onView={fetchDetails}
                      t={t}
                    />
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Box
                sx={{
                  textAlign: "center",
                  py: 8,
                  px: 3,
                  bgcolor: theme.palette.background.paper,
                  borderRadius: 2,
                  border: `1px dashed ${theme.palette.divider}`,
                }}
              >
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: 64,
                    color: theme.palette.text.disabled,
                    mb: 2,
                  }}
                >
                  🔍
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                  }}
                >
                  {name === t("myToDos.finalisedTab")
                    ? t("myToDos.noObservationsInStatus") ||
                      `No ${name.toLowerCase()} observations`
                    : t("myToDos.noObservationsInStatus") ||
                      `No ${t("myToDos.activeLabel")} observations`}
                </Typography>
                <Typography variant="body2" color="text.disabled">
                  {t("myToDos.noItemsMessage", {
                    type:
                      name === t("myToDos.finalisedTab")
                        ? t("myToDos.finalisedTab").toLowerCase()
                        : t("myToDos.activeLabel"),
                  })}
                </Typography>
              </Box>
            )}
          </TabPanel>
        ))}

        {tabIndex === 0 && hasMoreObservations && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              mt: 4,
              p: 3,
              bgcolor: theme.palette.background.paper,
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Button
              variant="contained"
              size="large"
              onClick={handleNext}
              disabled={!nextPageToken || isFetching}
              aria-label={t("myToDos.loadMoreAria")}
              startIcon={
                isFetching ? (
                  <CircularProgress size={20} color="inherit" />
                ) : null
              }
              sx={{
                borderRadius: 2,
                px: 4,
                py: 1.5,
                textTransform: "none",
                fontWeight: 600,
                fontSize: "1rem",
                boxShadow: theme.shadows[2],
                "&:hover": {
                  boxShadow: theme.shadows[4],
                  transform: "translateY(-1px)",
                },
                transition: "all 0.2s ease",
              }}
            >
              {isFetching
                ? t("myToDos.loading") || "Loading..."
                : t("myToDos.loadMore") || "Load More Tasks"}
            </Button>
          </Box>
        )}
      </Box>

      {selectedObs && (
        <ObservationDetailsDialog
          observation={selectedObs}
          onClose={handleDetailsClose}
          onActionSuccess={handleDetailsClose}
        />
      )}

      <Dialog
        maxWidth="lg"
        open={mapDialogOpen}
        onClose={handleCloseMapDialog}
        sx={{ "& .MuiDialog-paper": { width: "800px", borderRadius: 2 } }}
      >
        <DialogTitle>
          {t("myToDos.mapTitle")}
          <IconButton
            onClick={handleCloseMapDialog}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <APIProvider
            apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY}
            libraries={["places"]}
            onLoad={() => setMapsLoaded(true)}
          >
            <ObservationsMap
              mapsLoaded={mapsLoaded}
              observations={selectedObservations}
              markerLocations={markerLocations}
              onClose={handleCloseMapDialog}
              onMarkerClick={fetchDetails}
              t={t}
            />
          </APIProvider>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseMapDialog}>
            {t("myToDos.closeButton")}
          </Button>
        </DialogActions>
      </Dialog>
      <Toast
        open={toast.open}
        onClose={handleToastClose}
        severity={toast.severity}
        message={toast.message}
      />
    </>
  );
}
